import { useEffect } from 'react';
import { type UiEventKind, type UiEventMap, uiEventBus } from './ui-event-bus';

export type UseUiEventOptions = {
  /** When true, the most recent payload is delivered to the handler on mount. */
  replay?: boolean;
};

/**
 * Subscribe a React component to a `UiEventBus` channel. Returns nothing —
 * the handler is the side effect. The handler reference is captured per
 * render, so wrap in `useCallback` upstream when stability matters.
 */
export const useUiEvent = <K extends UiEventKind>(
  kind: K,
  handler: (payload: UiEventMap[K]) => void,
  opts: UseUiEventOptions = {},
): void => {
  useEffect(() => {
    if (opts.replay) {
      const last = uiEventBus.last(kind);
      if (last) handler(last);
    }
    return uiEventBus.subscribe(kind, handler);
    // Re-bind whenever kind/handler/replay changes; consumers stabilize handlers themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, handler, opts.replay]);
};
